import { countLabel } from '@micarahr/shared-utils';
import { differenceInHours, format, subDays } from 'date-fns';
import type {
  ApprovalItem,
  AttendanceTodaySummary,
  AttentionItem,
  PayrollLine,
  PayrollRun,
  PayrollRunStatus,
  PeopleMovementEntry,
  TimesheetRow,
  UpcomingHrEvent,
  WorkforceSummary,
} from '@micarahr/shared-types';
import {
  insert,
  paginate,
  readAll,
  readOne,
  update,
  type Page,
  type PageRequest,
} from './collections';
import { errors, runtime, serviceCall } from './runtime';
import { env } from '../config/env';
import { apiDashboardService } from './dashboard.api';
import { apiAttendanceAdminService } from './attendance-admin.api';
import { apiLeaveAdminService, apiOvertimeAdminService } from './approvals.api';
import {
  apiPayrollService,
  type NewPayrollPeriod,
  type PayrollPeriodException,
  type PayrollPeriodExclusion,
} from './payroll.api';
import { apiNotificationService } from './registries.api';
import { apiApprovalService } from './access.api';
import { apiScheduleService } from './schedules.api';
import {
  apiReportService,
  type AttendanceReport,
  type LeaveReport,
  type WorkforceReport,
} from './reports.api';
import {
  apiCorrectionAdminService,
  apiTimesheetListService,
  apiTimesheetService,
} from './timesheets.api';
import { recordAudit, type AuditActor } from './audit';
import { nextId } from './ids';
import { displayedDayStatus } from '../lib/labels';
import type { AdminEmployee } from './directory';
import type {
  AdminAttendanceRecord,
  AdminCorrectionRequest,
  AdminLeaveRequest,
  AdminOvertimeRequest,
} from './seed';

/**
 * Attendance, requests and payroll operations.
 *
 * The rule that shapes this whole module: an attendance *event* is immutable.
 * HR never rewrites what a device reported. A correction produces an adjustment
 * and an audit entry, and the original stays exactly as it was recorded — which
 * is the only way the record can still be trusted a year later (§14, §18).
 */

const todayIso = () => format(new Date(), 'yyyy-MM-dd');

/* -------------------------------------------------------------------------- */
/* Attendance                                                                  */
/* -------------------------------------------------------------------------- */

export interface AttendanceFilter extends PageRequest {
  employeeId?: string;
  date?: string;
  from?: string;
  to?: string;
  departmentId?: string;
  teamName?: string;
  managerName?: string;
  workLocationId?: string;
  status?: AdminAttendanceRecord['status'] | 'all';
  securityDecision?: AdminAttendanceRecord['securityDecision'] | 'all';
  flaggedOnly?: boolean;
  /** Where the day's review stands, as the row's "· awaiting review" suffix reads it. */
  review?: 'pending_review' | 'explanation_requested' | 'all';
  /**
   * Days that fell short, whatever their status: late, or left early. Its own
   * filter — a finished late day's status is "Completed", so offering "Late"
   * under Status listed rows whose Status column said otherwise.
   */
  shortfall?: 'late' | 'undertime' | 'all';
}

const mockAttendanceAdminService = {
  /** The prototype stores no photographs; only the API can hand one out. */
  async selfieUrl(_mediaId: string): Promise<string> {
    throw new Error('Selfies are only stored when MicaraHR is connected to its server.');
  },

  async list(filter: AttendanceFilter): Promise<Page<AdminAttendanceRecord>> {
    return serviceCall('attendanceAdminService', () => {
      let rows = readAll<AdminAttendanceRecord>('attendanceRecords');

      if (filter.employeeId) rows = rows.filter((row) => row.employeeId === filter.employeeId);
      if (filter.date) rows = rows.filter((row) => row.date === filter.date);
      if (filter.from) rows = rows.filter((row) => row.date >= filter.from!);
      if (filter.to) rows = rows.filter((row) => row.date <= filter.to!);
      if (filter.departmentId)
        rows = rows.filter((row) => row.departmentId === filter.departmentId);
      if (filter.teamName) rows = rows.filter((row) => row.teamName === filter.teamName);
      if (filter.managerName) rows = rows.filter((row) => row.managerName === filter.managerName);
      if (filter.workLocationId) {
        rows = rows.filter((row) => row.workLocationId === filter.workLocationId);
      }
      if (filter.status && filter.status !== 'all') {
        rows = rows.filter((row) => displayedDayStatus(row) === filter.status);
      }
      if (filter.securityDecision && filter.securityDecision !== 'all') {
        rows = rows.filter((row) => row.securityDecision === filter.securityDecision);
      }
      if (filter.flaggedOnly) rows = rows.filter((row) => row.riskFlags.length > 0);
      if (filter.review && filter.review !== 'all') {
        rows = rows.filter((row) => row.reviewState === filter.review);
      }
      if (filter.shortfall === 'late') rows = rows.filter((row) => row.lateMinutes > 0);
      if (filter.shortfall === 'undertime') rows = rows.filter((row) => row.undertimeMinutes > 0);

      return paginate(
        rows as unknown as Record<string, unknown>[],
        { sortBy: 'employeeName', ...filter },
        (row) => `${row.employeeName} ${row.employeeNumber} ${row.departmentName}`,
      ) as unknown as Page<AdminAttendanceRecord>;
    });
  },

  async get(id: string): Promise<AdminAttendanceRecord | null> {
    return serviceCall('attendanceAdminService', () =>
      readOne<AdminAttendanceRecord>('attendanceRecords', id),
    );
  },

  async todaySummary(): Promise<AttendanceTodaySummary> {
    return serviceCall('attendanceAdminService', () => {
      const rows = readAll<AdminAttendanceRecord>('attendanceRecords').filter(
        (row) => row.date === todayIso() && row.status !== 'rest_day',
      );
      return {
        scheduled: rows.length,
        timedIn: rows.filter((row) => row.timeIn !== null).length,
        notTimedIn: rows.filter(
          (row) => row.timeIn === null && !['absent', 'on_leave'].includes(row.status),
        ).length,
        timedInUnscheduled: 0,
        late: rows.filter((row) => row.lateMinutes > 0).length,
        absent: rows.filter((row) => row.status === 'absent').length,
        onLeave: rows.filter((row) => row.status === 'on_leave').length,
        pendingReview: rows.filter((row) => row.status === 'pending_review').length,
        awaitingReview: rows.filter((row) => row.reviewState === 'pending_review').length,
        flagged: rows.filter((row) => row.riskFlags.length > 0).length,
      };
    });
  },

  /**
   * Record an HR decision on a flagged attendance event.
   *
   * The decision is written onto the record; the timestamps and evidence are
   * untouched. Rejecting an event marks it for correction rather than deleting
   * it, because a deleted event cannot be explained to the employee.
   */
  // The prototype's decision. Against the API this is POST /attendance/:id/review
  // (attendance-admin.api.ts); `_revision` is the API's concurrency check.
  async review(
    id: string,
    decision:
      'approve' | 'approve_with_note' | 'request_explanation' | 'return_for_correction' | 'reject',
    note: string,
    actor: AuditActor,
    _revision?: number,
  ): Promise<AdminAttendanceRecord> {
    return serviceCall('attendanceAdminService', async () => {
      if (decision !== 'approve' && !note.trim()) {
        throw errors.validation(
          'A note is required for this decision so the employee can see why.',
        );
      }
      const before = readOne<AdminAttendanceRecord>('attendanceRecords', id);
      if (!before) throw errors.notFound('attendance record');

      const securityDecision: AdminAttendanceRecord['securityDecision'] =
        decision === 'approve'
          ? 'accepted'
          : decision === 'approve_with_note'
            ? 'accepted_with_note'
            : decision === 'reject'
              ? 'flagged'
              : 'pending_review';

      const status: AdminAttendanceRecord['status'] =
        decision === 'approve' || decision === 'approve_with_note'
          ? before.lateMinutes > 0
            ? 'late'
            : 'completed'
          : 'pending_review';

      const after = await update<AdminAttendanceRecord>(
        'attendanceRecords',
        id,
        (row) => ({
          ...row,
          securityDecision,
          status,
          hrNote: note || row.hrNote,
          reviewedByName: actor.name,
          reviewedAt: runtime.now(),
        }),
        'attendance record',
      );

      await recordAudit({
        actor,
        module: 'attendance',
        action: `attendance.${decision}`,
        summary: `${decisionLabel(decision)} attendance for ${after.employeeName} on ${after.date}`,
        recordType: 'attendance_record',
        recordId: id,
        recordLabel: `${after.employeeName} — ${after.date}`,
        subjectEmployeeId: after.employeeId,
        before: { status: before.status, securityDecision: before.securityDecision },
        after: { status, securityDecision },
        metadata: { note },
        severity: 'notice',
      });

      await notify(
        after.employeeId,
        'attendance',
        'Your attendance was reviewed',
        `${decisionLabel(decision)} for ${after.date}. ${note}`.trim(),
      );

      return after;
    });
  },

  addNote: addAttendanceNote,
  markAbsent: markAttendanceAbsent,
};

/**
 * Add an HR note to an attendance record.
 *
 * Notes are additive and attributed. They never replace what the employee said
 * and never touch the recorded events — a note is HR's account of a day, beside
 * the device's, not instead of it.
 */
// The prototype's note. Against the API: POST /attendance/:id/notes.
export async function addAttendanceNote(
  id: string,
  note: string,
  actor: AuditActor,
): Promise<AdminAttendanceRecord> {
  return serviceCall('attendanceAdminService', async () => {
    if (!note.trim()) throw errors.validation('A note cannot be empty.');

    const before = readOne<AdminAttendanceRecord>('attendanceRecords', id);
    if (!before) throw errors.notFound('attendance record');

    const stamped = `${actor.name}: ${note}`;
    const after = await update<AdminAttendanceRecord>(
      'attendanceRecords',
      id,
      (row) => ({ ...row, hrNote: row.hrNote ? `${row.hrNote}\n${stamped}` : stamped }),
      'attendance record',
    );

    await recordAudit({
      actor,
      module: 'attendance',
      action: 'attendance.note_added',
      summary: `Added an HR note to ${after.employeeName}'s attendance on ${after.date}`,
      recordType: 'attendance_record',
      recordId: id,
      recordLabel: `${after.employeeName} — ${after.date}`,
      subjectEmployeeId: after.employeeId,
      metadata: { note },
    });

    return after;
  });
}

/**
 * Mark a day absent.
 *
 * Refused where attendance was actually recorded. Overwriting a day someone
 * demonstrably worked is a payroll error and an accusation at the same time; if
 * the record is wrong, the correction workflow exists for exactly that.
 */
// The prototype's absence. Against the API: POST /attendance/:id/mark-absent.
export async function markAttendanceAbsent(
  id: string,
  reason: string,
  actor: AuditActor,
): Promise<AdminAttendanceRecord> {
  return serviceCall('attendanceAdminService', async () => {
    if (!reason.trim()) {
      throw errors.validation('Marking someone absent requires a reason.');
    }

    const before = readOne<AdminAttendanceRecord>('attendanceRecords', id);
    if (!before) throw errors.notFound('attendance record');

    if (before.timeIn !== null) {
      throw errors.precondition(
        'This employee recorded a time in for this day, so it cannot be marked absent. Use an attendance correction instead — it keeps what was recorded and adds the adjustment beside it.',
      );
    }

    const after = await update<AdminAttendanceRecord>(
      'attendanceRecords',
      id,
      (row) => ({
        ...row,
        status: 'absent',
        hrNote: `Marked absent by ${actor.name}. ${reason}`,
        reviewedByName: actor.name,
        reviewedAt: runtime.now(),
      }),
      'attendance record',
    );

    await recordAudit({
      actor,
      module: 'attendance',
      action: 'attendance.marked_absent',
      summary: `Marked ${after.employeeName} absent on ${after.date}`,
      recordType: 'attendance_record',
      recordId: id,
      recordLabel: `${after.employeeName} — ${after.date}`,
      subjectEmployeeId: after.employeeId,
      before: { status: before.status },
      after: { status: 'absent' },
      metadata: { reason },
      severity: 'sensitive',
    });

    await notify(
      after.employeeId,
      'attendance',
      'Your attendance was marked absent',
      `${after.date}. ${reason}`,
    );

    return after;
  });
}

function decisionLabel(decision: string): string {
  switch (decision) {
    case 'approve':
      return 'Approved';
    case 'approve_with_note':
      return 'Approved with a note';
    case 'request_explanation':
      return 'Requested an explanation';
    case 'return_for_correction':
      return 'Returned for correction';
    case 'reject':
      return 'Rejected';
    default:
      return 'Reviewed';
  }
}

/* -------------------------------------------------------------------------- */
/* Requests: leave, overtime, corrections                                      */
/* -------------------------------------------------------------------------- */

type RequestStatus = AdminLeaveRequest['status'];

export interface RequestFilter extends PageRequest {
  status?: RequestStatus | 'all' | 'open';
  departmentId?: string;
  employeeId?: string;
  from?: string;
  to?: string;
}

const OPEN_STATUSES: RequestStatus[] = ['pending_manager', 'pending_hr', 'returned'];

function filterRequests<
  T extends { status: RequestStatus; departmentId: string; employeeId: string },
>(rows: T[], filter: RequestFilter): T[] {
  let working = rows;
  if (filter.status === 'open') {
    working = working.filter((row) => OPEN_STATUSES.includes(row.status));
  } else if (filter.status && filter.status !== 'all') {
    working = working.filter((row) => row.status === filter.status);
  }
  if (filter.departmentId)
    working = working.filter((row) => row.departmentId === filter.departmentId);
  if (filter.employeeId) working = working.filter((row) => row.employeeId === filter.employeeId);
  return working;
}

const mockLeaveAdminService = {
  async list(filter: RequestFilter): Promise<Page<AdminLeaveRequest>> {
    return serviceCall('leaveAdminService', () => {
      let rows = filterRequests(readAll<AdminLeaveRequest>('leaveRequests'), filter);
      if (filter.from) rows = rows.filter((row) => row.endDate >= filter.from!);
      if (filter.to) rows = rows.filter((row) => row.startDate <= filter.to!);
      rows.sort((a, b) => b.submittedAt.localeCompare(a.submittedAt));
      return paginate(
        rows as unknown as Record<string, unknown>[],
        filter,
        (row) => `${row.employeeName} ${row.referenceCode} ${row.leaveTypeName} ${row.reason}`,
      ) as unknown as Page<AdminLeaveRequest>;
    });
  },

  async get(id: string): Promise<AdminLeaveRequest | null> {
    return serviceCall('leaveAdminService', () => readOne<AdminLeaveRequest>('leaveRequests', id));
  },

  async dashboard() {
    return serviceCall('leaveAdminService', () => {
      const rows = readAll<AdminLeaveRequest>('leaveRequests');
      const today = todayIso();
      return {
        pendingApprovals: rows.filter((row) => OPEN_STATUSES.includes(row.status)).length,
        onLeaveToday: rows.filter(
          (row) => row.status === 'approved' && row.startDate <= today && row.endDate >= today,
        ).length,
        upcoming: rows.filter((row) => row.status === 'approved' && row.startDate > today).length,
        needingHr: rows.filter((row) => row.status === 'pending_hr').length,
        withConflicts: rows.filter((row) => row.teamConflictCount > 0).length,
      };
    });
  },

  async decide(
    id: string,
    decision: 'approve' | 'reject' | 'return',
    comment: string,
    actor: AuditActor,
    adjustedDays?: number,
  ): Promise<AdminLeaveRequest> {
    return serviceCall('leaveAdminService', async () => {
      if (decision !== 'approve' && !comment.trim()) {
        throw errors.validation('A comment is required so the employee knows why.');
      }
      const before = readOne<AdminLeaveRequest>('leaveRequests', id);
      if (!before) throw errors.notFound('leave request');
      if (!OPEN_STATUSES.includes(before.status)) {
        throw errors.precondition('This request has already been decided.');
      }

      const status: RequestStatus =
        decision === 'approve' ? 'approved' : decision === 'reject' ? 'rejected' : 'returned';

      const after = await update<AdminLeaveRequest>(
        'leaveRequests',
        id,
        (row) => ({
          ...row,
          status,
          decidedAt: runtime.now(),
          decidedByName: actor.name,
          hrComment: comment || row.hrComment,
          totalDays: adjustedDays ?? row.totalDays,
          // Without a starting balance there is no ending balance to state.
          balanceAfter:
            row.balanceBefore === null ? null : row.balanceBefore - (adjustedDays ?? row.totalDays),
        }),
        'leave request',
      );

      await recordAudit({
        actor,
        module: 'leave',
        action: `leave.${decision}d`,
        summary: `${decision === 'approve' ? 'Approved' : decision === 'reject' ? 'Rejected' : 'Returned'} ${after.referenceCode} for ${after.employeeName}`,
        recordType: 'leave_request',
        recordId: id,
        recordLabel: after.referenceCode,
        subjectEmployeeId: after.employeeId,
        before: { status: before.status, totalDays: before.totalDays },
        after: { status, totalDays: after.totalDays },
        metadata: { comment, adjustedDays },
        severity: 'notice',
      });

      await notify(
        after.employeeId,
        'leave',
        `Leave ${status}`,
        `${after.leaveTypeName} ${after.startDate} — ${after.endDate}. ${comment}`.trim(),
      );

      return after;
    });
  },
};

const mockOvertimeAdminService = {
  async list(filter: RequestFilter): Promise<Page<AdminOvertimeRequest>> {
    return serviceCall('overtimeAdminService', () => {
      const rows = filterRequests(readAll<AdminOvertimeRequest>('overtimeRequests'), filter);
      rows.sort((a, b) => b.submittedAt.localeCompare(a.submittedAt));
      return paginate(
        rows as unknown as Record<string, unknown>[],
        filter,
        (row) => `${row.employeeName} ${row.referenceCode} ${row.overtimeType} ${row.reason}`,
      ) as unknown as Page<AdminOvertimeRequest>;
    });
  },

  async get(id: string): Promise<AdminOvertimeRequest | null> {
    return serviceCall('overtimeAdminService', () =>
      readOne<AdminOvertimeRequest>('overtimeRequests', id),
    );
  },

  async decide(
    id: string,
    decision: 'approve' | 'reject' | 'return',
    comment: string,
    actor: AuditActor,
    approvedHours?: number,
  ): Promise<AdminOvertimeRequest> {
    return serviceCall('overtimeAdminService', async () => {
      if (decision !== 'approve' && !comment.trim()) {
        throw errors.validation('A comment is required so the employee knows why.');
      }
      const before = readOne<AdminOvertimeRequest>('overtimeRequests', id);
      if (!before) throw errors.notFound('overtime request');

      const status: RequestStatus =
        decision === 'approve' ? 'approved' : decision === 'reject' ? 'rejected' : 'returned';

      const after = await update<AdminOvertimeRequest>(
        'overtimeRequests',
        id,
        (row) => ({
          ...row,
          status,
          approvedHours: decision === 'approve' ? (approvedHours ?? row.estimatedHours) : null,
          decidedAt: runtime.now(),
          decidedByName: actor.name,
        }),
        'overtime request',
      );

      await recordAudit({
        actor,
        module: 'overtime',
        action: `overtime.${decision}d`,
        summary: `${decision === 'approve' ? 'Approved' : decision === 'reject' ? 'Rejected' : 'Returned'} ${after.referenceCode} for ${after.employeeName}`,
        recordType: 'overtime_request',
        recordId: id,
        recordLabel: after.referenceCode,
        subjectEmployeeId: after.employeeId,
        before: { status: before.status, approvedHours: before.approvedHours },
        after: { status, approvedHours: after.approvedHours },
        metadata: { comment },
        severity: 'notice',
      });

      await notify(after.employeeId, 'overtime', `Overtime ${status}`, comment);
      return after;
    });
  },

  async markPayrollReady(id: string, actor: AuditActor): Promise<AdminOvertimeRequest> {
    return serviceCall('overtimeAdminService', async () => {
      const after = await update<AdminOvertimeRequest>(
        'overtimeRequests',
        id,
        (row) => {
          if (row.status !== 'approved') {
            throw errors.precondition('Only approved overtime can be marked payroll ready.');
          }
          return { ...row, payrollReady: true };
        },
        'overtime request',
      );
      await recordAudit({
        actor,
        module: 'overtime',
        action: 'overtime.payroll_ready',
        summary: `Marked ${after.referenceCode} ready for payroll`,
        recordType: 'overtime_request',
        recordId: id,
        recordLabel: after.referenceCode,
        subjectEmployeeId: after.employeeId,
      });
      return after;
    });
  },
};

const mockCorrectionAdminService = {
  async list(filter: RequestFilter): Promise<Page<AdminCorrectionRequest>> {
    return serviceCall('correctionAdminService', () => {
      const rows = filterRequests(readAll<AdminCorrectionRequest>('attendanceCorrections'), filter);
      rows.sort((a, b) => b.submittedAt.localeCompare(a.submittedAt));
      return paginate(
        rows as unknown as Record<string, unknown>[],
        filter,
        (row) => `${row.employeeName} ${row.referenceCode} ${row.reasonLabel} ${row.explanation}`,
      ) as unknown as Page<AdminCorrectionRequest>;
    });
  },

  async get(id: string): Promise<AdminCorrectionRequest | null> {
    return serviceCall('correctionAdminService', () =>
      readOne<AdminCorrectionRequest>('attendanceCorrections', id),
    );
  },

  /**
   * Approving a correction creates an *adjustment*.
   *
   * The original attendance record keeps its recorded timestamps; the approved
   * values are written alongside them and the record is marked as adjusted. An
   * auditor can still see what the device reported and what HR decided.
   */
  async decide(
    id: string,
    decision: 'approve' | 'reject' | 'return',
    comment: string,
    actor: AuditActor,
  ): Promise<AdminCorrectionRequest> {
    return serviceCall('correctionAdminService', async () => {
      if (decision !== 'approve' && !comment.trim()) {
        throw errors.validation('A comment is required so the employee knows why.');
      }
      const before = readOne<AdminCorrectionRequest>('attendanceCorrections', id);
      if (!before) throw errors.notFound('correction request');

      const status: RequestStatus =
        decision === 'approve' ? 'approved' : decision === 'reject' ? 'rejected' : 'returned';

      const after = await update<AdminCorrectionRequest>(
        'attendanceCorrections',
        id,
        (row) => ({
          ...row,
          status,
          decidedAt: runtime.now(),
          decidedByName: actor.name,
          hrComment: comment || row.hrComment,
        }),
        'correction request',
      );

      if (decision === 'approve') {
        const record = readOne<AdminAttendanceRecord>(
          'attendanceRecords',
          before.attendanceRecordId,
        );
        if (record) {
          await update<AdminAttendanceRecord>(
            'attendanceRecords',
            record.id,
            (row) => ({
              ...row,
              // The adjustment is additive. `events` — what the device actually
              // reported — is never rewritten.
              timeOut: before.requestedTimeOut ?? row.timeOut,
              status: row.status === 'missing_time_out' ? 'completed' : row.status,
              hrNote: `Adjusted by ${actor.name} through correction ${before.referenceCode}.`,
              reviewedByName: actor.name,
              reviewedAt: runtime.now(),
            }),
            'attendance record',
          );
        }
      }

      await recordAudit({
        actor,
        module: 'attendance',
        action: `correction.${decision}d`,
        summary: `${decision === 'approve' ? 'Approved' : decision === 'reject' ? 'Rejected' : 'Returned'} correction ${after.referenceCode} for ${after.employeeName}`,
        recordType: 'attendance_correction',
        recordId: id,
        recordLabel: after.referenceCode,
        subjectEmployeeId: after.employeeId,
        before: { status: before.status },
        after: { status, adjustedRecordId: before.attendanceRecordId },
        metadata: { comment },
        severity: 'notice',
      });

      await notify(after.employeeId, 'attendance', `Attendance correction ${status}`, comment);
      return after;
    });
  },
};

/* -------------------------------------------------------------------------- */
/* Timesheets                                                                  */
/* -------------------------------------------------------------------------- */

const mockTimesheetService = {
  async list(
    filter: PageRequest & {
      readiness?: TimesheetRow['readiness'] | 'all';
      departmentName?: string;
      periodId?: string;
    },
  ): Promise<Page<TimesheetRow>> {
    return serviceCall('timesheetService', () => {
      let rows = readAll<TimesheetRow>('timesheets');
      if (filter.periodId) rows = rows.filter((row) => row.periodId === filter.periodId);
      if (filter.readiness && filter.readiness !== 'all') {
        rows = rows.filter((row) => row.readiness === filter.readiness);
      }
      if (filter.departmentName) {
        rows = rows.filter((row) => row.departmentName === filter.departmentName);
      }
      return paginate(
        rows as unknown as Record<string, unknown>[],
        { sortBy: 'employeeName', ...filter },
        (row) => `${row.employeeName} ${row.employeeNumber} ${row.departmentName}`,
      ) as unknown as Page<TimesheetRow>;
    });
  },

  /**
   * The days behind a timesheet total.
   *
   * A period row says an employee worked 168 of 176 hours. The only useful
   * follow-up question is *which days*, and answering it from the attendance
   * records rather than a stored breakdown means the two can never disagree.
   */
  async daysFor(employeeId: string, from: string, to: string): Promise<AdminAttendanceRecord[]> {
    return serviceCall('timesheetService', () =>
      readAll<AdminAttendanceRecord>('attendanceRecords')
        .filter(
          (record) => record.employeeId === employeeId && record.date >= from && record.date <= to,
        )
        .sort((a, b) => a.date.localeCompare(b.date)),
    );
  },

  /**
   * Whether the period can be locked, and which period it is.
   *
   * "Blocked" is the number that decides whether payroll can run at all, and
   * reading it off a paginated list of a hundred-odd employees is not a
   * reasonable way to answer that. The period label is looked up from the
   * payroll run sharing this `periodId` — a page of totals that never says
   * which weeks it covers is a page of unattributed numbers.
   */
  /** The periods the timesheets belong to, newest first, named from the pay run. */
  async periods(): Promise<
    { id: string; label: string; startDate: string; endDate: string; locked: boolean }[]
  > {
    return serviceCall('timesheetService', () => {
      const rows = readAll<TimesheetRow>('timesheets');
      const runs = readAll<PayrollRun>('payrollRuns');
      return [...new Set(rows.map((row) => row.periodId))]
        .map((id) => {
          const run = runs.find((entry) => entry.periodId === id);
          return {
            id,
            label: run?.label ?? id,
            startDate: run?.periodStart ?? '',
            endDate: run?.periodEnd ?? '',
            locked: rows.some((row) => row.periodId === id && row.lockedAt !== null),
          };
        })
        .sort((a, b) => b.startDate.localeCompare(a.startDate));
    });
  },

  async summary(requestedPeriodId?: string) {
    return serviceCall('timesheetService', () => {
      const all = readAll<TimesheetRow>('timesheets');
      const periodId = (requestedPeriodId ?? all[0]?.periodId ?? null) as string | null;
      const rows = all.filter((row) => row.periodId === periodId);
      const run = periodId
        ? (readAll<PayrollRun>('payrollRuns').find((entry) => entry.periodId === periodId) ?? null)
        : null;

      return {
        periodId,
        periodLabel: run?.label ?? null,
        periodStart: run?.periodStart ?? null,
        periodEnd: run?.periodEnd ?? null,
        total: rows.length,
        ready: rows.filter((row) => row.readiness === 'ready').length,
        needsReview: rows.filter((row) => row.readiness === 'needs_review').length,
        inProgress: rows.filter((row) => row.readiness === 'in_progress').length,
        blocked: rows.filter((row) => row.readiness === 'blocked').length,
        missingLogs: rows.reduce((total, row) => total + row.missingLogCount, 0),
        awaitingReviewDays: rows.reduce((total, row) => total + (row.reviewPendingCount ?? 0), 0),
        awaitingReviewTimesheets: rows.filter((row) => row.readiness === 'needs_review').length,
        locked: rows.some((row) => row.lockedAt !== null),
      };
    });
  },

  /** The browser store has no attendance to rebuild from; its rows are what they are. */
  async regenerate(
    periodId: string,
    _actor: AuditActor,
  ): Promise<{ ready: number; blocked: number; needsReview: number; inProgress: number }> {
    return serviceCall('timesheetService', () => {
      const rows = readAll<TimesheetRow>('timesheets').filter((row) => row.periodId === periodId);
      return {
        ready: rows.filter((row) => row.readiness === 'ready').length,
        blocked: rows.filter((row) => row.readiness === 'blocked').length,
        needsReview: rows.filter((row) => row.readiness === 'needs_review').length,
        inProgress: rows.filter((row) => row.readiness === 'in_progress').length,
      };
    });
  },

  async lockPeriod(periodId: string, actor: AuditActor, _expectedCount?: number): Promise<number> {
    return serviceCall('timesheetService', async () => {
      const rows = readAll<TimesheetRow>('timesheets').filter((row) => row.periodId === periodId);
      const blocked = rows.filter((row) => row.readiness === 'blocked');
      if (blocked.length > 0) {
        throw errors.precondition(
          `${countLabel(blocked.length, 'timesheet')} still ${blocked.length === 1 ? 'has' : 'have'} missing logs. Resolve them before locking the period.`,
        );
      }
      const { writeAll } = await import('./collections');
      const all = readAll<TimesheetRow>('timesheets').map((row) =>
        row.periodId === periodId
          ? { ...row, readiness: 'locked' as const, lockedAt: runtime.now() }
          : row,
      );
      await writeAll('timesheets', all);

      await recordAudit({
        actor,
        module: 'timesheets',
        action: 'timesheet.period_locked',
        summary: `Locked ${countLabel(rows.length, 'timesheet')} for ${periodId}`,
        recordType: 'timesheet_period',
        recordId: periodId,
        recordLabel: periodId,
        metadata: { count: rows.length },
        severity: 'sensitive',
      });

      return rows.length;
    });
  },
};

/* -------------------------------------------------------------------------- */
/* Scheduling                                                                  */
/* -------------------------------------------------------------------------- */

export interface Shift {
  id: string;
  name: string;
  code: string;
  start: string;
  end: string;
  breakMinutes: number;
  gracePeriodMinutes: number;
  nightShift: boolean;
  active: boolean;
}

export interface ScheduleCell {
  employeeId: string;
  employeeName: string;
  departmentName: string;
  date: string;
  shiftId: string | null;
  shiftName: string | null;
  kind: 'workday' | 'rest_day' | 'holiday' | 'unassigned';
  /** An assignment exists for the day (a calendar holiday may have none). */
  assigned: boolean;
  /** Employees can see this day's assignment. A plan, still editable. */
  published: boolean;
  /** Attendance is recorded for the day. Locked: the past is evidence. */
  settled: boolean;
  /**
   * The employee's first day, when this day is before it. Nobody can be
   * scheduled before they start; the day is shown, greyed, and cannot be
   * selected. Absent on a day they are employed.
   */
  startsOn?: string | null;
  /**
   * The employee's last working day, when this day is after it. Nothing is
   * scheduled once they have left; the day is shown greyed as "Left" and
   * cannot be selected.
   */
  leftAfter?: string | null;
}

export interface CompanyHoliday {
  /** YYYY-MM-DD. */
  date: string;
  name: string;
}

export interface NewShift {
  code: string;
  name: string;
  start: string;
  end: string;
  breakMinutes: number;
  gracePeriodMinutes: number;
}

const mockScheduleService = {
  async shifts(): Promise<Shift[]> {
    return serviceCall('scheduleService', () => readAll<Shift>('shifts'));
  },

  /** The company holiday calendar: every non-working day the organisation observes. */
  async holidays(): Promise<CompanyHoliday[]> {
    return serviceCall('scheduleService', () =>
      readAll<CompanyHoliday>('holidays').map((row) => ({ date: row.date, name: row.name })),
    );
  },

  /**
   * The planner grid.
   *
   * Built from the attendance records that already exist for past days and the
   * employee's default shift for future ones, so the planner shows what
   * actually happened where it is known and what is intended where it is not —
   * rather than presenting a plan that quietly contradicts the record.
   */
  async grid(from: string, to: string, departmentId?: string): Promise<ScheduleCell[]> {
    return serviceCall('scheduleService', () => {
      const shifts = readAll<Shift>('shifts');
      const holidays = new Set(readAll<{ date: string }>('holidays').map((row) => row.date));
      const records = readAll<AdminAttendanceRecord>('attendanceRecords').filter(
        (record) => record.date >= from && record.date <= to,
      );

      const byKey = new Map(
        records.map((record) => [`${record.employeeId}:${record.date}`, record]),
      );
      const publishedKeys = new Set(
        readAll<{ employeeId: string; date: string; publishedAt?: string | null }>(
          'scheduleAssignments',
        )
          .filter((row) => row.publishedAt)
          .map((row) => `${row.employeeId}:${row.date}`),
      );

      let employees = readAll<AdminEmployee>('employees').filter(
        (employee) =>
          employee.employmentStatus === 'active' || employee.employmentStatus === 'probationary',
      );
      if (departmentId) {
        employees = employees.filter((employee) => employee.departmentId === departmentId);
      }

      const dates: string[] = [];
      for (
        let cursor = new Date(from);
        cursor <= new Date(to);
        cursor.setDate(cursor.getDate() + 1)
      ) {
        dates.push(cursor.toISOString().slice(0, 10));
      }

      const cells: ScheduleCell[] = [];
      for (const employee of employees) {
        for (const date of dates) {
          const record = byKey.get(`${employee.id}:${date}`);
          const weekend = [0, 6].includes(new Date(`${date}T00:00:00`).getDay());
          const shift = shifts.find((entry) => entry.id === employee.shiftId) ?? null;

          const kind: ScheduleCell['kind'] = holidays.has(date)
            ? 'holiday'
            : record
              ? record.status === 'rest_day'
                ? 'rest_day'
                : 'workday'
              : weekend
                ? 'rest_day'
                : 'workday';

          cells.push({
            employeeId: employee.id,
            employeeName: employee.fullName,
            departmentName: employee.departmentName,
            date,
            shiftId: kind === 'workday' ? (shift?.id ?? null) : null,
            shiftName: kind === 'workday' ? (shift?.name ?? null) : null,
            kind,
            // Anything with an attendance record is settled; future days are a plan.
            settled: record !== undefined,
            assigned: true,
            published: publishedKeys.has(`${employee.id}:${date}`),
          });
        }
      }

      return cells;
    });
  },

  /**
   * Assign a shift across a set of days.
   *
   * Refuses to touch a day that already has an attendance record: changing the
   * schedule under a recorded day would make the record look like a violation
   * of a rule that did not exist when it happened.
   */
  async assign(
    employeeIds: string[],
    dates: string[],
    shiftId: string | null,
    actor: AuditActor,
  ): Promise<{ assigned: number; skipped: number }> {
    return serviceCall('scheduleService', async () => {
      if (employeeIds.length === 0 || dates.length === 0) {
        throw errors.validation('Choose at least one employee and one day.');
      }

      const records = readAll<AdminAttendanceRecord>('attendanceRecords');
      const settled = new Set(records.map((record) => `${record.employeeId}:${record.date}`));

      let assigned = 0;
      let skipped = 0;
      const assignments = readAll<{
        id: string;
        employeeId: string;
        date: string;
        shiftId: string | null;
      }>('scheduleAssignments');
      const next = [...assignments];

      for (const employeeId of employeeIds) {
        for (const date of dates) {
          if (settled.has(`${employeeId}:${date}`)) {
            skipped += 1;
            continue;
          }
          const existing = next.findIndex(
            (entry) => entry.employeeId === employeeId && entry.date === date,
          );
          const row = { id: nextId('sch'), employeeId, date, shiftId };
          if (existing === -1) next.push(row);
          else next[existing] = { ...next[existing]!, shiftId };
          assigned += 1;
        }
      }

      const { writeAll } = await import('./collections');
      await writeAll('scheduleAssignments', next);

      await recordAudit({
        actor,
        module: 'schedules',
        action: shiftId === null ? 'schedule.rest_day_assigned' : 'schedule.assigned',
        summary:
          shiftId === null
            ? `Set ${countLabel(assigned, 'day')} as rest days for ${countLabel(employeeIds.length, 'employee')}`
            : `Assigned a shift across ${countLabel(assigned, 'day')} for ${countLabel(employeeIds.length, 'employee')}`,
        recordType: 'schedule',
        recordId: 'schedule',
        recordLabel: `${dates[0]} – ${dates[dates.length - 1]}`,
        metadata: { assigned, skipped, shiftId },
        severity: 'notice',
      });

      return { assigned, skipped };
    });
  },

  /** Publish (or withdraw) the drafts in a range, mirroring the API's shape. */
  async setPublished(
    from: string,
    to: string,
    published: boolean,
    employeeIds?: string[],
  ): Promise<{ changed: number; skippedSettled: number }> {
    return serviceCall('scheduleService', async () => {
      const rows = readAll<{ employeeId: string; date: string; publishedAt?: string | null }>(
        'scheduleAssignments',
      );
      let changed = 0;
      const next = rows.map((row) => {
        if (row.date < from || row.date > to || Boolean(row.publishedAt) === published) return row;
        if (employeeIds && !employeeIds.includes(row.employeeId)) return row;
        changed += 1;
        return { ...row, publishedAt: published ? new Date().toISOString() : null };
      });
      const { writeAll } = await import('./collections');
      await writeAll('scheduleAssignments', next);
      return { changed, skippedSettled: 0 };
    });
  },

  async createShift(input: NewShift): Promise<Shift> {
    return serviceCall('scheduleService', async () => {
      const shift: Shift = {
        id: nextId('shf'),
        ...input,
        nightShift: input.end < input.start,
        active: true,
      };
      const { writeAll } = await import('./collections');
      await writeAll('shifts', [...readAll<Shift>('shifts'), shift]);
      return shift;
    });
  },
};

/**
 * Scheduling. The grid, the templates and assignment come from the API when
 * connected; the settled-day rule is enforced server-side either way.
 */
export const scheduleService: typeof mockScheduleService =
  env().dataSource === 'api'
    ? { ...mockScheduleService, ...apiScheduleService }
    : mockScheduleService;

/* -------------------------------------------------------------------------- */
/* Payroll                                                                     */
/* -------------------------------------------------------------------------- */

/** Legal transitions. A run cannot skip from draft to published (§21). */
const PAYROLL_TRANSITIONS: Record<PayrollRunStatus, PayrollRunStatus[]> = {
  draft: ['attendance_review'],
  attendance_review: ['input_review', 'draft'],
  input_review: ['calculating'],
  calculating: ['calculated'],
  calculated: ['for_approval', 'input_review'],
  for_approval: ['approved', 'input_review'],
  approved: ['locked'],
  locked: ['payslips_published', 'reopened'],
  payslips_published: ['completed', 'reopened'],
  completed: ['reopened'],
  reopened: ['input_review'],
};

export function canTransition(from: PayrollRunStatus, to: PayrollRunStatus): boolean {
  return (PAYROLL_TRANSITIONS[from] ?? []).includes(to);
}

const mockPayrollService = {
  /*
   * Exceptions and exclusions are the engine's; the browser-only store keeps
   * exceptions on its lines and has nothing to resolve them against.
   */
  async exceptions(_periodId: string): Promise<PayrollPeriodException[]> {
    return [];
  },

  async exclusions(_periodId: string): Promise<PayrollPeriodExclusion[]> {
    return [];
  },

  async resolveException(_periodId: string, _exceptionId: string, _note: string): Promise<unknown> {
    throw errors.precondition('Resolving payroll exceptions needs the payroll engine.');
  },

  async excludeEmployee(_periodId: string, _employeeId: string, _reason: string): Promise<unknown> {
    throw errors.precondition('Excluding an employee needs the payroll engine.');
  },

  async includeEmployee(_periodId: string, _employeeId: string): Promise<unknown> {
    throw errors.precondition('Including an employee needs the payroll engine.');
  },

  /** A draft period in the browser-only store; the API adapter is the real one. */
  async create(input: NewPayrollPeriod): Promise<PayrollRun> {
    return serviceCall('payrollService', async () => {
      const zero = { minorUnits: 0, currency: 'PHP' as const };
      const run = {
        id: nextId('run'),
        periodId: nextId('period'),
        ...input,
        payrollGroupName: 'Monthly — All employees',
        status: 'draft',
        totals: {
          employeeCount: 0,
          excludedCount: 0,
          grossPay: zero,
          totalDeductions: zero,
          totalContributions: zero,
          totalTax: zero,
          netPay: zero,
          exceptionCount: 0,
          warningCount: 0,
        },
        attendanceImportedAt: null,
        calculatedAt: null,
        submittedAt: null,
        approvedAt: null,
        approvedByUserId: null,
        lockedAt: null,
        publishedAt: null,
        reopenReason: null,
        createdAt: runtime.now(),
      } as PayrollRun;
      await insert('payrollRuns', run);
      return run;
    });
  },

  async listRuns(): Promise<PayrollRun[]> {
    return serviceCall('payrollService', () =>
      readAll<PayrollRun>('payrollRuns').sort((a, b) => b.periodEnd.localeCompare(a.periodEnd)),
    );
  },

  async getRun(id: string): Promise<PayrollRun | null> {
    return serviceCall('payrollService', () => readOne<PayrollRun>('payrollRuns', id));
  },

  async lines(
    runId: string,
    filter: PageRequest & { exceptionsOnly?: boolean },
  ): Promise<Page<PayrollLine>> {
    return serviceCall('payrollService', () => {
      let rows = readAll<PayrollLine>('payrollLines').filter((row) => row.runId === runId);
      if (filter.exceptionsOnly) rows = rows.filter((row) => row.exceptions.length > 0);
      return paginate(
        rows as unknown as Record<string, unknown>[],
        { sortBy: 'employeeName', ...filter },
        (row) => `${row.employeeName} ${row.employeeNumber} ${row.departmentName}`,
      ) as unknown as Page<PayrollLine>;
    });
  },

  async advance(
    id: string,
    to: PayrollRunStatus,
    actor: AuditActor,
    reason?: string,
  ): Promise<PayrollRun> {
    return serviceCall('payrollService', async () => {
      const before = readOne<PayrollRun>('payrollRuns', id);
      if (!before) throw errors.notFound('payroll run');

      if (!canTransition(before.status, to)) {
        throw errors.precondition(
          `A payroll run cannot move from ${before.status.replace(/_/g, ' ')} to ${to.replace(/_/g, ' ')}.`,
        );
      }
      if (to === 'reopened' && !reason?.trim()) {
        throw errors.validation('Reopening a locked payroll run requires a reason.');
      }

      const now = runtime.now();
      const after = await update<PayrollRun>(
        'payrollRuns',
        id,
        (row) => ({
          ...row,
          status: to,
          calculatedAt: to === 'calculated' ? now : row.calculatedAt,
          submittedAt: to === 'for_approval' ? now : row.submittedAt,
          approvedAt: to === 'approved' ? now : row.approvedAt,
          approvedByUserId: to === 'approved' ? actor.userId : row.approvedByUserId,
          lockedAt: to === 'locked' ? now : row.lockedAt,
          publishedAt: to === 'payslips_published' ? now : row.publishedAt,
          reopenReason: to === 'reopened' ? (reason ?? null) : row.reopenReason,
        }),
        'payroll run',
      );

      await recordAudit({
        actor,
        module: 'payroll',
        action: `payroll.${to}`,
        summary: `${after.label} moved to ${to.replace(/_/g, ' ')}`,
        recordType: 'payroll_run',
        recordId: id,
        recordLabel: after.label,
        before: { status: before.status },
        after: { status: to },
        metadata: reason ? { reason } : {},
        severity: to === 'reopened' || to === 'payslips_published' ? 'critical' : 'sensitive',
      });

      return after;
    });
  },

  /**
   * Add an adjustment to one employee's calculation.
   *
   * Adjustments are additive rows with a reason, never an edit to a computed
   * figure — so the difference between "what the rules produced" and "what a
   * human decided" stays legible.
   */
  async addAdjustment(
    lineId: string,
    input: { kind: 'earning' | 'deduction'; label: string; amountPesos: number; reason: string },
    actor: AuditActor,
  ): Promise<PayrollLine> {
    return serviceCall('payrollService', async () => {
      if (!input.reason.trim()) throw errors.validation('An adjustment requires a reason.');
      if (input.amountPesos <= 0) throw errors.validation('An adjustment amount must be positive.');

      const line = readOne<PayrollLine>('payrollLines', lineId);
      if (!line) throw errors.notFound('payroll line');

      const run = readOne<PayrollRun>('payrollRuns', line.runId);
      if (run && ['locked', 'payslips_published', 'completed'].includes(run.status)) {
        throw errors.precondition(
          'This payroll run is locked. Reopen it with a reason before making adjustments.',
        );
      }

      const minorUnits = Math.round(input.amountPesos * 100);
      const after = await update<PayrollLine>(
        'payrollLines',
        lineId,
        (row) => ({
          ...row,
          adjustments: [
            ...row.adjustments,
            {
              id: nextId('adj'),
              kind: input.kind,
              label: input.label,
              amount: { minorUnits, currency: 'PHP' as const },
              reason: input.reason,
              createdByUserId: actor.userId,
              createdAt: runtime.now(),
              supportingDocumentName: null,
            },
          ],
          netPay: {
            minorUnits:
              row.netPay.minorUnits + (input.kind === 'earning' ? minorUnits : -minorUnits),
            currency: 'PHP' as const,
          },
        }),
        'payroll line',
      );

      await recordAudit({
        actor,
        module: 'payroll',
        action: 'payroll.adjustment_added',
        summary: `Added a ${input.kind} of ₱${input.amountPesos.toLocaleString()} for ${after.employeeName}`,
        recordType: 'payroll_line',
        recordId: lineId,
        recordLabel: `${after.employeeName} — ${after.employeeNumber}`,
        subjectEmployeeId: after.employeeId,
        after: { label: input.label, amountPesos: input.amountPesos },
        metadata: { reason: input.reason },
        severity: 'sensitive',
      });

      return after;
    });
  },
};

/* -------------------------------------------------------------------------- */
/* Approvals                                                                   */
/* -------------------------------------------------------------------------- */

const mockApprovalService = {
  /**
   * The global approval queue.
   *
   * A projection over the owning collections rather than a table of its own —
   * a second copy of a leave request is a second thing that can be stale.
   */
  async list(filter: {
    kind?: string;
    view?: 'assigned' | 'hr' | 'payroll' | 'completed' | 'overdue';
  }): Promise<ApprovalItem[]> {
    return serviceCall('approvalService', () => {
      const items: ApprovalItem[] = [];
      const now = new Date();

      for (const request of readAll<AdminLeaveRequest>('leaveRequests')) {
        items.push({
          id: `leave:${request.id}`,
          kind: 'leave',
          recordId: request.id,
          referenceCode: request.referenceCode,
          title: `${request.leaveTypeName} · ${countLabel(request.totalDays, 'day')}`,
          subjectEmployeeId: request.employeeId,
          subjectName: request.employeeName,
          submittedByName: request.employeeName,
          submittedAt: request.submittedAt,
          magnitude: countLabel(request.totalDays, 'day'),
          status: request.status as ApprovalItem['status'],
          currentStepLabel: request.status === 'pending_hr' ? 'HR review' : 'Manager review',
          assignedToUserId: null,
          dueDate: request.startDate,
          flag:
            request.teamConflictCount > 0
              ? `${countLabel(request.teamConflictCount, 'colleague')} already on leave`
              : null,
        });
      }

      for (const request of readAll<AdminOvertimeRequest>('overtimeRequests')) {
        items.push({
          id: `overtime:${request.id}`,
          kind: 'overtime',
          recordId: request.id,
          referenceCode: request.referenceCode,
          title: `${countLabel(request.estimatedHours, 'hour')} · ${request.overtimeType}`,
          subjectEmployeeId: request.employeeId,
          subjectName: request.employeeName,
          submittedByName: request.employeeName,
          submittedAt: request.submittedAt,
          magnitude: `${request.estimatedHours} h`,
          status: request.status as ApprovalItem['status'],
          currentStepLabel: 'Manager review',
          assignedToUserId: null,
          dueDate: request.date,
          flag: request.estimatedHours > 8 ? 'Above the 8-hour single-approval limit' : null,
        });
      }

      for (const request of readAll<AdminCorrectionRequest>('attendanceCorrections')) {
        items.push({
          id: `correction:${request.id}`,
          kind: 'attendance_correction',
          recordId: request.id,
          referenceCode: request.referenceCode,
          title: request.reasonLabel,
          subjectEmployeeId: request.employeeId,
          subjectName: request.employeeName,
          submittedByName: request.employeeName,
          submittedAt: request.submittedAt,
          magnitude: request.date,
          status: request.status as ApprovalItem['status'],
          currentStepLabel: request.status === 'pending_hr' ? 'HR review' : 'Manager review',
          assignedToUserId: null,
          dueDate: null,
          flag: null,
        });
      }

      let rows = items;
      if (filter.kind && filter.kind !== 'all')
        rows = rows.filter((row) => row.kind === filter.kind);

      if (filter.view === 'completed') {
        rows = rows.filter((row) => ['approved', 'rejected'].includes(row.status));
      } else if (filter.view === 'overdue') {
        rows = rows.filter(
          (row) =>
            OPEN_STATUSES.includes(row.status as RequestStatus) &&
            differenceInHours(now, new Date(row.submittedAt)) > 72,
        );
      } else if (filter.view === 'hr') {
        rows = rows.filter((row) => row.status === 'pending_hr');
      } else {
        rows = rows.filter((row) => OPEN_STATUSES.includes(row.status as RequestStatus));
      }

      return rows.sort((a, b) => a.submittedAt.localeCompare(b.submittedAt));
    });
  },
};

/**
 * Reports, from the prototype store.
 *
 * Counts every row rather than a page, which is free here because the whole
 * store is in memory — and is exactly what the API does with a `GROUP BY`.
 * The shapes match `reports.api.ts` so the screen has one interface.
 */
const mockReportService = {
  async workforce(): Promise<WorkforceReport> {
    return serviceCall('reportService', () => {
      const employees = readAll<AdminEmployee>('employees');
      const departments = new Map<string, number>();
      const statuses = new Map<string, number>();
      const types = new Map<string, number>();

      for (const employee of employees) {
        const name = employee.departmentName || 'Unassigned';
        departments.set(name, (departments.get(name) ?? 0) + 1);
        statuses.set(employee.employmentStatus, (statuses.get(employee.employmentStatus) ?? 0) + 1);
        types.set(employee.employmentType, (types.get(employee.employmentType) ?? 0) + 1);
      }

      return {
        headcount: employees.length,
        byDepartment: [...departments].map(([department, count]) => ({ department, count })),
        byStatus: [...statuses].map(([status, count]) => ({ status, count })),
        byType: [...types].map(([type, count]) => ({ type, count })),
        insufficientData: employees.length < 5,
      };
    });
  },

  async attendance(from: string, to: string): Promise<AttendanceReport> {
    return serviceCall('reportService', () => {
      const records = readAll<AdminAttendanceRecord>('attendanceRecords').filter(
        (row) => row.date >= from && row.date <= to,
      );

      // The API's split: the Status column's day status, and the Review filter's two states.
      const byStatus: Record<string, number> = {};
      const byReview = { pending_review: 0, explanation_requested: 0 };
      for (const record of records) {
        const shown = displayedDayStatus(record);
        if (shown) byStatus[shown] = (byStatus[shown] ?? 0) + 1;
        if (
          record.reviewState === 'pending_review' ||
          record.reviewState === 'explanation_requested'
        ) {
          byReview[record.reviewState] += 1;
        }
      }

      return {
        period: { from, to },
        recordCount: records.length,
        byStatus,
        byReview,
        totalLateMinutes: records.reduce((sum, row) => sum + row.lateMinutes, 0),
        totalUndertimeMinutes: records.reduce((sum, row) => sum + row.undertimeMinutes, 0),
        flaggedCount: records.filter((row) => row.riskScore > 0).length,
        insufficientData: records.length < 10,
      };
    });
  },

  async leave(): Promise<LeaveReport> {
    return serviceCall('reportService', () => {
      const requests = readAll<AdminLeaveRequest>('leaveRequests');

      const statuses = new Map<string, { count: number; days: number }>();
      const types = new Map<string, { count: number; days: number }>();

      for (const request of requests) {
        const status = statuses.get(request.status) ?? { count: 0, days: 0 };
        statuses.set(request.status, {
          count: status.count + 1,
          days: status.days + request.totalDays,
        });

        // Approved only: a rejected request consumed nobody's balance.
        if (request.status !== 'approved') continue;
        const type = types.get(request.leaveTypeName) ?? { count: 0, days: 0 };
        types.set(request.leaveTypeName, {
          count: type.count + 1,
          days: type.days + request.totalDays,
        });
      }

      return {
        byStatus: [...statuses].map(([status, value]) => ({
          status,
          count: value.count,
          days: String(value.days),
        })),
        byType: [...types].map(([name, value]) => ({
          leaveTypeId: name,
          name,
          count: value.count,
          days: String(value.days),
        })),
        insufficientData: requests.length < 5,
      };
    });
  },
};

export const reportService: typeof mockReportService =
  env().dataSource === 'api' ? apiReportService : mockReportService;

/**
 * The approval queue.
 *
 * Entirely the API's when connected, rather than spread over the prototype:
 * the prototype builds the queue by sweeping three collections, and merging
 * that with a server answer would produce a list that is neither.
 */
export const approvalService: typeof mockApprovalService =
  env().dataSource === 'api' ? apiApprovalService : mockApprovalService;

/* -------------------------------------------------------------------------- */
/* Dashboard                                                                   */
/* -------------------------------------------------------------------------- */

const mockDashboardService = {
  async workforce(): Promise<WorkforceSummary> {
    return serviceCall('dashboardService', () => {
      const employees = readAll<AdminEmployee>('employees');
      const monthStart = new Date();
      monthStart.setDate(1);
      return {
        totalEmployees: employees.length,
        activeEmployees: employees.filter((row) => row.employmentStatus === 'active').length,
        newHiresThisMonth: employees.filter(
          (row) => new Date(row.dateHired) >= monthStart && new Date(row.dateHired) <= new Date(),
        ).length,
        onProbation: employees.filter((row) => row.employmentStatus === 'probationary').length,
        onLeaveToday: employees.filter((row) => row.employmentStatus === 'on_leave').length,
        separationsInProgress: readAll<{ closedAt: string | null }>('offboardingCases').filter(
          (row) => row.closedAt === null,
        ).length,
      };
    });
  },

  /**
   * The attention centre.
   *
   * The dashboard's job is to answer "what needs HR today", so this is built
   * from the things that are actually stuck, sorted by how long they have been
   * stuck — not from a count of records that happen to exist.
   */
  async attention(): Promise<AttentionItem[]> {
    return serviceCall('dashboardService', () => {
      const items: AttentionItem[] = [];
      const now = new Date();
      const hoursSince = (iso: string) => Math.max(0, differenceInHours(now, new Date(iso)));

      for (const record of readAll<AdminAttendanceRecord>('attendanceRecords')) {
        if (record.status === 'missing_time_out') {
          items.push({
            id: `att_missing_${record.id}`,
            kind: 'missing_time_out',
            severity: 'medium',
            employeeId: record.employeeId,
            employeeName: record.employeeName,
            issue: `No time out recorded for ${record.date}`,
            ageHours: hoursSince(`${record.date}T18:00:00.000Z`),
            ownerLabel: 'HR',
            href: `/attendance?selected=${record.id}`,
          });
        }
        if (record.riskFlags.some((flag) => flag.code === 'outside_geofence')) {
          items.push({
            id: `att_geo_${record.id}`,
            kind: 'attendance_outside_geofence',
            severity: 'high',
            employeeId: record.employeeId,
            employeeName: record.employeeName,
            issue: `Attendance recorded away from ${record.workLocationName}`,
            ageHours: hoursSince(record.timeIn ?? `${record.date}T09:00:00.000Z`),
            ownerLabel: 'HR review',
            href: `/attendance/review?selected=${record.id}`,
          });
        }
      }

      for (const request of readAll<AdminCorrectionRequest>('attendanceCorrections')) {
        if (OPEN_STATUSES.includes(request.status)) {
          items.push({
            id: `cor_${request.id}`,
            kind: 'pending_correction',
            severity: 'medium',
            employeeId: request.employeeId,
            employeeName: request.employeeName,
            issue: `Correction ${request.referenceCode} waiting on ${request.status === 'pending_hr' ? 'HR' : 'the manager'}`,
            ageHours: hoursSince(request.submittedAt),
            ownerLabel: request.status === 'pending_hr' ? 'HR' : 'Manager',
            href: `/attendance/corrections?selected=${request.id}&status=open`,
          });
        }
      }

      for (const request of readAll<AdminLeaveRequest>('leaveRequests')) {
        if (request.status === 'pending_hr') {
          items.push({
            id: `lv_${request.id}`,
            kind: 'leave_pending_hr',
            severity: 'medium',
            employeeId: request.employeeId,
            employeeName: request.employeeName,
            issue: `${request.leaveTypeName} (${countLabel(request.totalDays, 'day')}) waiting for HR`,
            ageHours: hoursSince(request.submittedAt),
            ownerLabel: 'HR',
            href: `/leave?selected=${request.id}&status=open`,
          });
        }
      }

      for (const request of readAll<AdminOvertimeRequest>('overtimeRequests')) {
        if (OPEN_STATUSES.includes(request.status)) {
          items.push({
            id: `ot_${request.id}`,
            kind: 'overtime_pending',
            severity: 'low',
            employeeId: request.employeeId,
            employeeName: request.employeeName,
            issue: `${countLabel(request.estimatedHours, 'hour')} of overtime awaiting approval`,
            ageHours: hoursSince(request.submittedAt),
            ownerLabel: 'Manager',
            href: `/overtime?selected=${request.id}&status=open`,
          });
        }
      }

      for (const document of readAll<{
        id: string;
        employeeId: string;
        employeeName: string;
        name: string;
        status: string;
        expiresOn: string | null;
      }>('documents')) {
        if (document.status === 'expiring' || document.status === 'expired') {
          items.push({
            id: `doc_${document.id}`,
            kind: 'document_expiring',
            severity: document.status === 'expired' ? 'high' : 'low',
            employeeId: document.employeeId,
            employeeName: document.employeeName,
            issue: `${document.name} ${document.status === 'expired' ? 'has expired' : `expires ${document.expiresOn}`}`,
            ageHours: 0,
            ownerLabel: 'HR',
            href: `/documents?selected=${document.id}&status=expiring`,
          });
        }
      }

      for (const review of readAll<{
        id: string;
        employeeId: string;
        employeeName: string;
        reviewDueDate: string;
        hrDecision: string;
      }>('probationReviews')) {
        if (review.hrDecision === 'pending' && new Date(review.reviewDueDate) <= now) {
          items.push({
            id: `prb_${review.id}`,
            kind: 'probation_review_due',
            severity: 'medium',
            employeeId: review.employeeId,
            employeeName: review.employeeName,
            issue: `Probation review was due ${review.reviewDueDate}`,
            ageHours: hoursSince(`${review.reviewDueDate}T09:00:00.000Z`),
            ownerLabel: 'HR',
            href: `/probation?selected=${review.id}`,
          });
        }
      }

      return items.sort((a, b) => {
        const weight = { high: 0, medium: 1, low: 2 } as const;
        if (weight[a.severity] !== weight[b.severity])
          return weight[a.severity] - weight[b.severity];
        return b.ageHours - a.ageHours;
      });
    });
  },

  async peopleMovement(): Promise<PeopleMovementEntry[]> {
    return serviceCall('dashboardService', () => {
      const entries: PeopleMovementEntry[] = [];
      const cutoff = subDays(new Date(), 45);

      for (const employee of readAll<AdminEmployee>('employees')) {
        if (new Date(employee.dateHired) >= cutoff) {
          entries.push({
            id: `mv_hire_${employee.id}`,
            kind: 'new_hire',
            employeeId: employee.id,
            employeeName: employee.fullName,
            detail: `${employee.positionTitle} · ${employee.departmentName}`,
            effectiveDate: employee.dateHired,
          });
        }
      }
      for (const separation of readAll<{
        id: string;
        employeeId: string;
        employeeName: string;
        lastWorkingDay: string;
        reason: string;
      }>('offboardingCases')) {
        entries.push({
          id: `mv_sep_${separation.id}`,
          kind: 'resignation',
          employeeId: separation.employeeId,
          employeeName: separation.employeeName,
          detail: `Last working day ${separation.lastWorkingDay}`,
          effectiveDate: separation.lastWorkingDay,
        });
      }

      return entries.sort((a, b) => b.effectiveDate.localeCompare(a.effectiveDate)).slice(0, 12);
    });
  },

  async upcomingEvents(): Promise<UpcomingHrEvent[]> {
    return serviceCall('dashboardService', () => {
      const events: UpcomingHrEvent[] = [];
      const now = new Date();
      const withinDays = (date: string, days: number) => {
        const diff = (new Date(date).getTime() - now.getTime()) / 86_400_000;
        return diff >= 0 && diff <= days;
      };

      for (const employee of readAll<AdminEmployee>('employees')) {
        const birthday = `${now.getFullYear()}-${employee.birthDate.slice(5)}`;
        if (withinDays(birthday, 21)) {
          events.push({
            id: `ev_bd_${employee.id}`,
            kind: 'birthday',
            label: `${employee.preferredName}'s birthday`,
            detail: employee.departmentName,
            date: birthday,
            employeeId: employee.id,
          });
        }
        const anniversary = `${now.getFullYear()}-${employee.dateHired.slice(5)}`;
        if (withinDays(anniversary, 21)) {
          const years = now.getFullYear() - Number(employee.dateHired.slice(0, 4));
          if (years > 0) {
            events.push({
              id: `ev_an_${employee.id}`,
              kind: 'work_anniversary',
              label: `${employee.preferredName} — ${countLabel(years, 'year')}`,
              detail: employee.positionTitle,
              date: anniversary,
              employeeId: employee.id,
            });
          }
        }
        if (employee.contractEndDate && withinDays(employee.contractEndDate, 60)) {
          events.push({
            id: `ev_ce_${employee.id}`,
            kind: 'contract_expiry',
            label: `${employee.fullName} — contract ends`,
            detail: employee.positionTitle,
            date: employee.contractEndDate,
            employeeId: employee.id,
          });
        }
      }

      for (const holiday of readAll<{ id: string; name: string; date: string }>('holidays')) {
        if (withinDays(holiday.date, 45)) {
          events.push({
            id: `ev_hol_${holiday.id}`,
            kind: 'holiday',
            label: holiday.name,
            detail: null,
            date: holiday.date,
            employeeId: null,
          });
        }
      }

      return events.sort((a, b) => a.date.localeCompare(b.date)).slice(0, 12);
    });
  },
};

/* -------------------------------------------------------------------------- */
/* Notifications                                                               */
/* -------------------------------------------------------------------------- */

export interface WebNotification {
  id: string;
  category: string;
  title: string;
  body: string;
  href: string;
  read: boolean;
  createdAt: string;
  employeeId?: string;
}

async function notify(
  employeeId: string,
  category: string,
  title: string,
  body: string,
): Promise<void> {
  await insert<WebNotification>('notifications', {
    id: nextId('ntf'),
    category,
    title,
    body,
    href: '/',
    read: false,
    createdAt: runtime.now(),
    employeeId,
  });
}

const mockNotificationService = {
  async list(filter: { unreadOnly?: boolean; category?: string }): Promise<WebNotification[]> {
    return serviceCall('notificationService', () => {
      let rows = readAll<WebNotification>('notifications');
      if (filter.unreadOnly) rows = rows.filter((row) => !row.read);
      if (filter.category && filter.category !== 'all') {
        rows = rows.filter((row) => row.category === filter.category);
      }
      return rows.sort((a, b) => b.createdAt.localeCompare(a.createdAt));
    });
  },

  async unreadCount(): Promise<number> {
    return serviceCall(
      'notificationService',
      () => readAll<WebNotification>('notifications').filter((row) => !row.read).length,
    );
  },

  async markRead(id: string): Promise<void> {
    await serviceCall('notificationService', async () => {
      await update<WebNotification>(
        'notifications',
        id,
        (row) => ({ ...row, read: true }),
        'notification',
      );
    });
  },

  async markAllRead(): Promise<void> {
    await serviceCall('notificationService', async () => {
      const { writeAll } = await import('./collections');
      await writeAll(
        'notifications',
        readAll<WebNotification>('notifications').map((row) => ({ ...row, read: true })),
      );
    });
  },
};

/**
 * The dashboard adapter selector.
 *
 * Everything comes from GET /dashboards/hr: the counters, the attention
 * centre, and the movement and upcoming lists, which the server builds from
 * the same rows the counters count so the panels cannot contradict them.
 */
export const dashboardService =
  env().dataSource === 'api'
    ? { ...mockDashboardService, ...apiDashboardService }
    : mockDashboardService;

/**
 * Attendance operations adapter selector.
 *
 * Everything comes from the API: the list, detail, today's summary (GET
 * /dashboards/attendance), and the review decision, HR note and mark-absent
 * routes on a single record.
 */
export const attendanceAdminService =
  env().dataSource === 'api'
    ? { ...mockAttendanceAdminService, ...apiAttendanceAdminService }
    : mockAttendanceAdminService;

/**
 * A request queue whose `decide` answers with the outcome, not the record.
 *
 * The prototype returns the updated request; the server returns only the
 * decision and the status it moved to (marking overtime payroll-ready is the
 * same). Screens refetch the list after a decision either way, so nothing
 * reads the result — typing it as the record would claim a shape the API never
 * sends.
 */
type DecidingService<T, K extends string = 'decide'> = Omit<T, K> & {
  [M in K]: M extends keyof T
    ? T[M] extends (...args: infer A) => unknown
      ? (...args: A) => Promise<unknown>
      : never
    : never;
};

/** Leave and overtime queues. Decisions and lists come from the API. */
export const leaveAdminService: DecidingService<typeof mockLeaveAdminService> =
  env().dataSource === 'api'
    ? { ...mockLeaveAdminService, ...apiLeaveAdminService }
    : mockLeaveAdminService;

export const overtimeAdminService: DecidingService<
  typeof mockOvertimeAdminService,
  'decide' | 'markPayrollReady'
> =
  env().dataSource === 'api'
    ? { ...mockOvertimeAdminService, ...apiOvertimeAdminService }
    : mockOvertimeAdminService;

/**
 * Payroll operations.
 *
 * The lifecycle, period list and employee results come from the engine. The
 * prototype's exception helpers and adjustment editing stay beneath until each
 * is migrated — losing them would break the exception centre outright.
 */
export const payrollService =
  env().dataSource === 'api' ? { ...mockPayrollService, ...apiPayrollService } : mockPayrollService;

/** Notifications. The unread count is the server's, not the first page's. */
export const notificationService =
  env().dataSource === 'api'
    ? { ...mockNotificationService, ...apiNotificationService }
    : mockNotificationService;

/*
 * Timesheets and corrections.
 *
 * Annotated with the mock's type for the reason the organisation service is:
 * a spread of two objects whose methods share a signature infers as a union of
 * function types, which TanStack Query will not accept as a query function.
 */
export const timesheetService: typeof mockTimesheetService =
  env().dataSource === 'api'
    ? { ...mockTimesheetService, ...apiTimesheetService, ...apiTimesheetListService }
    : mockTimesheetService;

export const correctionAdminService: DecidingService<typeof mockCorrectionAdminService> =
  env().dataSource === 'api'
    ? { ...mockCorrectionAdminService, ...apiCorrectionAdminService }
    : mockCorrectionAdminService;
